import { useAtom } from "jotai";

import { SessionThreadAtom } from "../../components/Deepchat/state/DeepchatState";
import { ClarifyingAnswer } from "../../components/Deepchat/SessionThread/ClarifyingQuestions";
import { useDeepChatSocketContext } from "../../contexts/DeepChatSocketContext";
import { useFilterContext } from "../../contexts/FilterProvider";
import useDeepchat from "../../contexts/useDeepchat";
import { SearchFilters } from "../../models/search";

// Marker that separates the user's original query from the clarification context.
export const CLARIFICATION_CONTEXT_MARKER = "Additional context from my clarifications —";

/**
 * Reverse of the context clause built on submit: given a follow-up turn's query text (which
 * carries the appended clarification context) and a question, extract the values the user
 * chose for that question. Used to re-hydrate the answered clarification module when a
 * persisted thread is reopened and no localStorage draft exists.
 */
export const parseClarificationValues = (followUpQuery: string | undefined, questionText: string): string[] => {
  if (!followUpQuery || !questionText) return [];
  const markerIdx = followUpQuery.indexOf(CLARIFICATION_CONTEXT_MARKER);
  if (markerIdx === -1) return [];
  const clause = followUpQuery
    .slice(markerIdx + CLARIFICATION_CONTEXT_MARKER.length)
    .trim()
    .replace(/\.$/, "");
  // Each segment is "question: comma-separated values"; segments are separated by "; ".
  for (const segment of clause.split(";")) {
    const sep = segment.indexOf(":");
    if (sep === -1) continue;
    const label = segment.slice(0, sep).trim();
    if (label !== questionText.trim()) continue;
    return segment
      .slice(sep + 1)
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * Handles the user's response to a clarifying-question turn.
 *
 * Answered selections are appended to the user query as a short natural-language
 * context clause, so the LLM has a plain-text summary of what the user clarified
 */
export const useClarification = () => {
  const [session, setSession] = useAtom(SessionThreadAtom);
  const { filterState } = useFilterContext();
  const { askQuestionStreaming } = useDeepchat();
  const DeepChatSocket = useDeepChatSocketContext();

  // Turn the answered selections into a readable context clause for the LLM.
  const buildContextClause = (answers: ClarifyingAnswer[]): string => {
    const parts = answers
      .map((a) => {
        const pieces = [...a.values];
        if (a.freeText) pieces.push(a.freeText);
        if (pieces.length === 0) return null;
        return `${a.question || a.field_hint}: ${pieces.join(", ")}`;
      })
      .filter((part): part is string => part !== null);
    if (parts.length === 0) return "";
    return `${CLARIFICATION_CONTEXT_MARKER} ${parts.join("; ")}.`;
  };

  // Flag the most recent turn's clarification as handled so the module renders disabled.
  const markLastClarificationAnswered = () => {
    setSession((prev) => {
      const next = [...prev];
      const last = next.at(-1);
      if (last?.clarification) {
        next[next.length - 1] = { ...last, clarificationAnswered: true };
      }
      return next;
    });
  };

  // Re-send the query (optionally augmented with clarification context) using the given
  // filters. Loading, thinking-steps reset, and the optimistic pending item are handled
  // inside askQuestionStreaming.
  const resendQuery = (queryText: string, filters: SearchFilters) => {
    if (!queryText || !filters) return;

    askQuestionStreaming(DeepChatSocket.sendJsonMessage, filters, queryText);
  };

  const submitAnswers = (answers: ClarifyingAnswer[]) => {
    if (!filterState) return;

    const originalQuery = session.at(-1)?.user_query ?? "";
    if (!originalQuery) return;

    // Append all answered information — predefined selections and free-text "Other" entries
    // alike — as natural-language context for the LLM.
    const contextClause = buildContextClause(answers);
    const augmentedQuery = contextClause ? `${originalQuery}\n\n${contextClause}` : originalQuery;

    markLastClarificationAnswered();
    resendQuery(augmentedQuery, filterState);
  };

  const skipClarification = () => {
    if (!filterState) return;
    const originalQuery = session.at(-1)?.user_query ?? "";
    if (!originalQuery) return;

    markLastClarificationAnswered();
    resendQuery(originalQuery, filterState);
  };

  // Enhanced questions: the user picked (or completed) a more specific rephrasing. Send it
  // immediately as the new query rather than populating the input for manual editing. We mark
  // the clarification as answered so the module collapses into its confirmed state.
  const selectEnhancedPrompt = (prompt: string) => {
    if (!filterState || !prompt) return;
    markLastClarificationAnswered();
    resendQuery(prompt, filterState);
  };

  return { submitAnswers, skipClarification, selectEnhancedPrompt };
};
